<template>
  <section class="full-width-bleed bg-pale-blue expert-review" v-if="expertReviewData">
    <div class="container">
      <div :class="['expert-review__title-container', reviewItemStyles]">
        <h3 class="expert-review__title">{{ carData.Make }} {{ carData.Model }} review &nbsp;</h3>
        <a
          v-if="isRACQReview"
          :href="internalReviewLink"
          @click="redirectToExpertReview"
          class="expert-review__club-link racq-club-review"
          v-text="reviewProvidedByText"
        />
        <a
          v-else-if="externalReviewLink && externalReviewLink !== 'null'"
          :href="externalReviewLink"
          class="link expert-review__club-link"
          v-text="reviewProvidedByText"
        />
        <span v-else class="expert-review__club-link" v-text="reviewProvidedByText" />
      </div>

      <div class="expert-review__article">
        <p class="expert-review__subtitle" v-text="expertReviewData.ArticleGrab" />
        <div class="expert-review__content" v-html="expertReviewData.ArticleBody"></div>
      </div>

      <CarDetailExpertReviewStats
        v-if="objectHasEntries(expertReviewData)"
        :car-data="carData"
        :api-status="apiStatus"
        :expert-review-data="expertReviewData"
      />
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { IExpertReview } from '../../foundation/interfaces/IExpertReview';
import { ApiStatusTypes } from '../../foundation/types/apiStatusTypes';
import CarDetailExpertReviewStats from './CarDetailExpertReviewStats.vue';
import { objectHasEntries } from '../../foundation/helpers';
import { useUrlBuilder } from '../../composables/useUrlBuilder';

const props = defineProps<{
  expertReviewData: Partial<IExpertReview>;
  apiStatus: ApiStatusTypes;
  carData: any;
}>();

const urlBuilder = useUrlBuilder();
const reviewItemStyles = computed(() => (props.apiStatus === 'LOADING' ? 'skeleton-box' : ''));
const isRACQReview = computed(() => props.expertReviewData.MobilityClub === 'RACQ');
const reviewProvidedByText = computed(() => `Review provided by ${props.expertReviewData.MobilityClub}`);
const externalReviewLink = computed(() => props.expertReviewData.RelatedLinks?.OriginalContentLocation);
const internalReviewLink = computed(() =>
  urlBuilder.getExpertReviewsUrl(
    props.carData.Vehicle_UID as string,
    props.expertReviewData.Id as string,
    `${props.carData?.Year_low}-${props.carData?.Year_high}`,
    props.carData?.Make,
    props.carData?.Model
  )
);

function redirectToExpertReview() {
  window.location.href = internalReviewLink.value;
}
</script>

<style lang="scss" scoped>
.expert-review {
  padding: 2rem 0;

  @media screen and (min-width: #{$breakpoint-md}) {
    padding: 3rem 0 4rem;
  }

  &__title-container {
    padding: 0 0 1rem;
    display: flex;
    flex-direction: column;

    @media screen and (min-width: #{$breakpoint-md}) {
      justify-content: space-between;
      align-items: center;
      flex-direction: row;
    }

    @media screen and (min-width: #{$breakpoint-xl}) {
      padding: 0;
    }
  }

  &__title,
  &__content,
  &__subtitle,
  &__club-link {
    &.loading {
      height: 5rem;
      margin-bottom: 1rem;
      color: transparent;
    }
  }

  &__club-link {
    text-decoration: none;
    font-size: 0.875rem;
  }

  &__title {
    font-size: 2rem;
    margin-top: 1rem;
    margin-bottom: 0.75rem;

    @media screen and (min-width: #{$breakpoint-lg}) {
      margin-bottom: 1.75rem;
    }
  }

  &__subtitle {
    font-size: 1.25rem;
  }

  &__content {
    padding-bottom: 0.5rem;
  }
}

.loading * {
  color: transparent;
  background: transparent;
  visibility: hidden;
}
</style>
